import type { PropsWithChildren } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Avatar,
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Text,
  Tab,
  TabList,
  Tooltip,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  DataTrending24Regular,
  KeyMultiple24Regular,
  Key24Regular,
  DocumentText24Regular,
  People24Regular,
  SignOut24Regular,
  WeatherMoon24Regular,
  WeatherSunny24Regular,
  LocalLanguage24Regular,
} from '@fluentui/react-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useThemeMode } from '../theme/context.tsx';
import { useI18n } from '../i18n/context.tsx';

const useStyles = makeStyles({
  root: {
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
    minHeight: '100vh',
    background: tokens.colorNeutralBackground2,
  },
  topbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '12px 24px',
    background: tokens.colorNeutralBackground1,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  logo: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
  },
  spacer: { flex: 1 },
  body: {
    ...shorthands.padding('24px', '32px'),
    maxWidth: '1440px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    boxSizing: 'border-box',
  },
  tabs: { paddingLeft: '8px' },
  headerControls: { display: 'flex', gap: '4px', alignItems: 'center' },
});

type AppShellProps = PropsWithChildren<{ isAdmin?: boolean }>;

export function AppShell({ children, isAdmin = false }: AppShellProps) {
  const styles = useStyles();
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, toggle } = useThemeMode();
  const { locale, setLocale, t } = useI18n();
  const account = accounts[0];

  const tabs = [
    { id: '/dashboard', label: t('nav', 'dashboard'), icon: <DataTrending24Regular /> },
    { id: '/shared-keys', label: t('nav', 'sharedKeys'), icon: <KeyMultiple24Regular /> },
    { id: '/parent-keys', label: t('nav', 'parentKeys'), icon: <Key24Regular /> },
    { id: '/logs', label: t('nav', 'logs'), icon: <DocumentText24Regular /> },
    // Users page is admin-only — viewers don't get a tab because they'd just
    // hit a 403 on every action.
    ...(isAdmin
      ? [{ id: '/users', label: t('nav', 'users'), icon: <People24Regular /> }]
      : []),
  ];

  return (
    <div className={styles.root}>
      <div className={styles.topbar}>
        <Text className={styles.logo}>LINE Ads API Proxy</Text>
        <TabList
          className={styles.tabs}
          selectedValue={tabs.find((ti) => location.pathname.startsWith(ti.id))?.id ?? '/dashboard'}
          onTabSelect={(_, d) => navigate(String(d.value))}
        >
          {tabs.map((ti) => (
            <Tab key={ti.id} value={ti.id} icon={ti.icon}>
              {ti.label}
            </Tab>
          ))}
        </TabList>
        <div className={styles.spacer} />
        <div className={styles.headerControls}>
          <Tooltip content={t('common', 'darkMode')} relationship="label">
            <Button
              appearance="subtle"
              icon={mode === 'dark' ? <WeatherSunny24Regular /> : <WeatherMoon24Regular />}
              onClick={toggle}
            />
          </Tooltip>
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Tooltip content={t('common', 'language')} relationship="label">
                <Button appearance="subtle" icon={<LocalLanguage24Regular />}>
                  {locale === 'zh' ? '中' : 'EN'}
                </Button>
              </Tooltip>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => setLocale('zh')}>中文</MenuItem>
                <MenuItem onClick={() => setLocale('en')}>English</MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                icon={<Avatar name={account?.name ?? account?.username} size={28} />}
              >
                {account?.name ?? account?.username}
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem
                  icon={<SignOut24Regular />}
                  onClick={() => instance.logoutRedirect().catch(console.error)}
                >
                  {t('common', 'signOut')}
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </div>
      <main className={styles.body}>{children}</main>
    </div>
  );
}
